import React, { useState, useEffect, useRef } from "react";
import { clearSession } from "../../shared/_helpers/session";
import { logOut } from "../../shared/_services/auth.services";
import { Link, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { getOnboardingDetails, getNotifications, markNotificationSeen } from "../../shared/_services/common.services";
import Notifications from "../Notifications";

import { useSelector } from "react-redux";
import { selectUnread } from "../../redux/threadSlice";

import chatAnimatedIcon from "../../assets/images/chatAnimatedIcon.gif";

import {
  Button, Dialog, DialogContent, DialogTitle, DialogDescription, DialogActions, DialogClose,
} from "../ui";
import StarsIcon from "../../styles/StarsIcon";
import { useAuth } from "../../context/AuthContext.jsx";
import { hasChatAccess } from "../../shared/_helpers/chatGate";
import { LINKED_ACCOUNTS_PATH } from "../plaid/plaidNav";
import { LINKED_TITLE } from "../plaid/plaidCopy";

function DashboardHeader() {
  const navigate = useNavigate();
  const newUnreadMessage = useSelector(selectUnread);
  const { enqueueSnackbar } = useSnackbar();
  const [notificationPopupShow, setNotificationPopupShow] = useState(false);
  const [confirmLogoutPopupShow, setConfirmLogoutPopupShow] = useState(false);
  const confirmLogoutPopupClose = () => setConfirmLogoutPopupShow();
  const [notification, setNotification] = useState([]);
  const [newNotificaiton, setNewNotification] = useState(false);
  const [videoPopupShow, setVideoPopupShow] = useState(false);
  const videoPopupClose = () => setVideoPopupShow(false);
  const videoPopupOpen = () => setVideoPopupShow(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const chatRef = useRef(null);

  const { user } = useAuth();

  // Close chatbot on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatRef.current && !chatRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  const openConfirmLogoutPopup = () => {
    setConfirmLogoutPopupShow(true);
    setIsMenuOpen(false);
  };

  const handleVideoClick = () => {
    videoPopupOpen();
  }

  const handleLogout = async () => {
    // logOut clears the stored session itself, whatever the server answered,
    // so this always navigates. Gating the clear on a 200 is what made logout
    // unusable: the request had the wrong shape for SEC-115 (#72), so the
    // branch never ran and the only feedback was an error toast. Even with the
    // right shape a 15-minute access token can expire in an open tab, and a
    // logout that refuses to work because the server said 401 is a worse
    // outcome than an orphaned refresh token nobody holds a copy of.
    const data = await logOut();

    if (data?.status !== 200) {
      enqueueSnackbar("Signed out on this device, but the server could not end the session.", {
        variant: "warning",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    }

    navigate("/login");
  };

  const handleNotificationsClick = async () => {
    setNotificationPopupShow(true);
    setNewNotification(false)

    for (const notif of notification) {
      try {
        await markNotificationSeen(notif._id);
      } catch (error) {
        console.error(`Error marking notification ${notif._id} as seen:`, error);
      }
    }
  };


  const getData = async () => {
    let data = await getOnboardingDetails();

    if (data.status !== 200) {
      if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired!", {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
      }
    }
  };

  const getNotificationData = async () => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const userId = currentUser?.user?._id
    const data = await getNotifications(userId);

    if (data.status === 200) {
      const unseenNotifications = data?.data?.data?.filter(notification => !notification.seen);
      setNotification(unseenNotifications);
      if (unseenNotifications.length > 0) {
        setNewNotification(true)
      }
    }
    else {
      if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("No New Notifications", {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
       
      }
    }
  }

 

  const handleNotificationClose = (state) => {
    setNotificationPopupShow(state)
  }

  useEffect(() => {
    getNotificationData();
    getData();
  }, []); // eslint-disable-line

  // Pooled inbox SP1 (W4) / SP2-W14 (owner decision B): the unread badge
  // comes from ReadState — the client's thread is unread when
  // lastAdvisorMessageAt > lastReadAt, computed by threadSlice's
  // selectUnread. DashboardSidebar (mounted on every DashboardLayout route)
  // now owns loading the thread and the live "message received" listener, so
  // this badge just reads the shared state reactively.

  const name = `${JSON.parse(localStorage.getItem("currentUser"))?.user?.firstName} ${JSON.parse(localStorage.getItem("currentUser"))?.user?.lastName}`;

  return (
    <>
    <div className="tw-mb-8">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
        <div className="tw-min-w-0">
          <h5 className="tw-m-0 tw-text-xl tw-font-bold tw-text-text">Welcome,&nbsp;
            <span title={name}>
              {name.slice(0, 20) +
                ((JSON.parse(localStorage.getItem("currentUser"))?.user?.firstName + JSON.parse(localStorage.getItem("currentUser"))?.user?.lastName).length > 20 ? "..." : "")
              }
            </span>
          </h5>
          <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-text-muted">Here is your Financial Overview</p>
        </div>
        <div className="tw-ml-auto tw-flex tw-flex-wrap tw-items-center tw-gap-2">

          {/* SP6 (owner item 13): the old Configure Plaid entry, its own copy of
              the link flow, is gone; seeing linked banks is never plan-gated. */}
          <Button as={Link} variant="ghost" size="sm" to={LINKED_ACCOUNTS_PATH}>
            <StarsIcon name="bank" className="tw-h-4 tw-w-4" />
            <span>{LINKED_TITLE}</span>
          </Button>

          <Button variant="icon" iconOnly onClick={handleVideoClick} title="Video" aria-label="Video">
            <StarsIcon name="video" className="tw-h-5 tw-w-5 tw-shrink-0" />
          </Button>
          {/* SP4 chat gate (decision 29): no way into the thread before a plan is paid. */}
          {hasChatAccess(user) && (
            <Button
              as={Link}
              variant="icon"
              iconOnly
              className="tw-relative"
              to="/thread"
              title="Inbox"
              aria-label="Inbox"
              data-unread={newUnreadMessage ? "true" : undefined}
            >
              <img className="tw-h-6 tw-w-6" src={chatAnimatedIcon} alt="" />
              {newUnreadMessage && (
                <span data-slot="dot" className="tw-absolute tw-right-1 tw-top-1 tw-h-2 tw-w-2 tw-rounded-pill tw-bg-text-accent tw-ring-2 tw-ring-surface" />
              )}
            </Button>
          )}
          <Button
            variant="icon"
            iconOnly
            className="tw-relative"
            onClick={handleNotificationsClick}
            title="Notifications"
            aria-label="Notifications"
            data-unread={newNotificaiton ? "true" : undefined}
          >
            <StarsIcon name="bell" className="tw-h-5 tw-w-5 tw-shrink-0" />
            {newNotificaiton && (
              <span data-slot="dot" className="tw-absolute tw-right-1 tw-top-1 tw-h-2 tw-w-2 tw-rounded-pill tw-bg-text-accent tw-ring-2 tw-ring-surface" />
            )}
          </Button>
          <Notifications onClose={handleNotificationClose} showModal={notificationPopupShow} />

          {/* Logout Button */}
          <Button variant="icon" iconOnly onClick={openConfirmLogoutPopup} title="Logout" aria-label="Logout">
            <StarsIcon name="logout" className="tw-h-5 tw-w-5 tw-shrink-0" />
          </Button>
        </div>
      </div>

      {/* confirm logout popup (the old modal was backdrop="static" keyboard={false}) */}
      <Dialog open={confirmLogoutPopupShow} onOpenChange={(o) => !o && confirmLogoutPopupClose()}>
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogClose asChild>
              <Button variant="icon" size="sm" iconOnly aria-label="Close">
                <StarsIcon name="x" className="tw-h-4 tw-w-4" />
              </Button>
            </DialogClose>
          </div>
          <DialogDescription>You are attempting to logout of Astra.</DialogDescription>
          <DialogActions>
            <Button onClick={handleLogout}>
              <StarsIcon name="check" className="tw-h-4 tw-w-4" />
              Logout
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <Dialog open={videoPopupShow} onOpenChange={(o) => !o && videoPopupClose()}>
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <div>
              <DialogTitle>Welcome to Astra</DialogTitle>
              <DialogDescription>Your Quick Start Guide</DialogDescription>
            </div>
            <DialogClose asChild>
              <Button variant="icon" size="sm" iconOnly aria-label="Close">
                <StarsIcon name="x" className="tw-h-4 tw-w-4" />
              </Button>
            </DialogClose>
          </div>
          <div className="tw-mt-4">
            <video className="tw-block tw-w-full tw-rounded-md" src={`${process.env.REACT_APP_BACKEND}/uploads/WelcometoAstraStars.mp4`} controls loop autoPlay></video>
          </div>
        </DialogContent>
      </Dialog>
    </div>

    </>
  );
}

export default DashboardHeader;
